import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { Component, ComponentRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Observable } from "rxjs";
import { map, debounceTime, distinctUntilChanged } from "rxjs/operators";
import { CanComponentDeactivate } from "../../../../security/unsaved-changes.guard";
import { MatDialog } from "@angular/material/dialog";
import { MatStepper } from "@angular/material/stepper";
import { Router } from "@angular/router";
import { DropDownModel } from "../../../../domain/model/common/dropDownModel";
import { EnrollmentEntityRentalPropertyLicense } from "../../../../domain/model/enrollment/enrollmentEntityRentalPropertyLicense";
import { EnrollmentRentalProperty } from "../../../../domain/model/enrollment/enrollmentRentalProperty";
import { EnrollmentRentalPropertyLicense } from "../../../../domain/model/enrollment/enrollmentRentalPropertyLicense";
import { EnrollmentTaxRepresentative } from "../../../../domain/model/enrollment/enrollmentTaxRepresentative";
import { PropertyEnrollment } from "../../../../domain/model/enrollment/propertyEnrollment";
import { PropertyOwner } from "../../../../domain/model/enrollment/propertyOwner";
import { NotificationType } from "../../../../domain/model/enums/notificationType";
import { CommonServices } from "../../../../domain/service/commonService";
import { EnrollmentService } from "../../../../domain/service/enrollmentService";
import { GooglePlacesService } from "../../../../domain/service/GooglePlaceService";
import { NotificationComponent } from "../../../../shared/notification/notification.component";
import { atLeastOneRequiredValidator } from "../../../../shared/utils/atLeastOneRequiredValidator";
import { DialogConfirmComponent } from '../../dialog-confirm/dialog-confirm.component';
import { EntitylicensestepComponent } from "../shared/entitylicensestep/entitylicensestep.component";
import { RentalPropertyLicenseComponent } from "../shared/rental-property-license/rental-property-license.component";
import { RentalPropertyReportingFormComponent } from "../shared/rental-property-reporting-form/rental-property-reporting-form.component";
import { RentalPropertyComponent } from "../shared/rental-property/rental-property.component";

@Component({
  selector: "app-add-property-managment-company",
  templateUrl: "./add-property-managment-company.component.html",
  styleUrl: "./add-property-managment-company.component.css",
})
export class AddPropertyManagmentCompanyComponent implements OnInit, CanComponentDeactivate {
  private overlayRef!: OverlayRef;
  @ViewChild("entitylicensestep") entitylicensestep: EntitylicensestepComponent | undefined;
  @ViewChild("rentalPropertyLicenseComponent") rentalPropertyLicenseComponent:
    | RentalPropertyLicenseComponent
    | undefined;
  @ViewChild("rentalPropertyComponent") rentalPropertyComponent: RentalPropertyComponent | undefined;
  @ViewChild("rentalPropertyReportingFormComponent") rentalPropertyReportingFormComponent:
    | RentalPropertyReportingFormComponent
    | undefined;
  suggestions: any[] = [];
  constructor(
    private googlePlacesService: GooglePlacesService,
    private overlay: Overlay,
    private readonly router: Router,
    public dialogRef: MatDialog,
    private commonServices: CommonServices,
    private enrollment: EnrollmentService,
    private _formBuilder: FormBuilder
  ) {
    this.mgtmCoAddress.get("businnesStreetAddress")?.valueChanges.subscribe((value) => {
      if (value) {
        this.googlePlacesService.getPlaces(value).then((predictions: any) => {
          this.suggestions = predictions;
        });
      } else {
        this.suggestions = [];
      }
    });

    this.ownerForm.get("ownerStreetAddress")?.valueChanges.subscribe((value) => {
      if (value) {
        this.googlePlacesService.getPlaces(value).then((predictions: any) => {
          this.suggestions = predictions;
        });
      } else {
        this.suggestions = [];
      }
    });

    this.wireZipAutofill(this.mgtmCoAddress);
    this.wireZipAutofill(this.ownerForm);
  }

  /**
   * When a 5-digit zip is entered, look up city/state and fill them in.
   * Uses { emitEvent: false } so filling fields doesn't re-trigger lookups.
   */
  private wireZipAutofill(form: FormGroup): void {
    form
      .get("zipCode")
      ?.valueChanges.pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(async (zip: string | null) => {
        const loc = await this.googlePlacesService.getLocationByZip(zip ?? "");
        if (!loc) {
          return;
        }
        if (loc.city) {
          form.get("city")?.setValue(loc.city, { emitEvent: false });
        }
        if (loc.stateCode) {
          const stateId = this.states.find((e) => e.iso2 == loc.stateCode)?.id;
          if (stateId) {
            form.get("state")?.setValue(stateId, { emitEvent: false });
          }
        }
      });
  }

  states: DropDownModel[] = [];
  countries: DropDownModel[] = [];
  showTaxRepresentativeForm: boolean = false;
  showCityLocalRequirements = false;
  addressType: string = "1";
  stepCompleted: boolean = false;
  mgtmCoAddress = new FormGroup({
    businessLegalName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
    businessDbaName: new FormControl(""),
    webUrl: new FormControl("", [
      Validators.pattern(/^(https?:\/\/|www\.)[a-zA-Z0-9\-\.]+(?:\.[a-zA-Z]{2,})(?:\/[a-zA-Z0-9\-]+)*\/?$/),
    ]),
    businnesStreetAddress: new FormControl("", [Validators.required]),
    aptUnitSuite: new FormControl(""),
    city: new FormControl("", Validators.required),
    state: new FormControl(0, Validators.required),
    zipCode: new FormControl("", [
      Validators.required,
      Validators.maxLength(12),
      Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/),
    ]),
    note: new FormControl(""),
  });

  ownerForm = new FormGroup({
    firstName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
    lastName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
    email: new FormControl("", [Validators.required, Validators.maxLength(320), Validators.email]),
    phoneNumber: new FormControl("", [
      Validators.required,
      Validators.maxLength(100),
      Validators.pattern(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/),
    ]),
    note: new FormControl(""),
    ownerStreetAddress: new FormControl("", Validators.required),
    aptUnitSuite: new FormControl(""),
    city: new FormControl("", Validators.required),
    state: new FormControl(0, [Validators.required, Validators.min(1)]),
    zipCode: new FormControl("", [
      Validators.required,
      Validators.maxLength(12),
      Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/),
    ]),
    addressName: new FormControl(""),
    addressOrPoBox: new FormControl(""),
    cityStateProvince: new FormControl(""),
    country: new FormControl(0),
  });
 
  mgtnCoReporting = new FormGroup({
    isUsingTaxRepresentative: new FormControl(null),
    sendToOwner: new FormControl(),
    sendToReportingRep: new FormControl(),
    taxRepresentativeFirstName: new FormControl(),
    taxRepresentativeLastName: new FormControl(),
    taxRepresentativeEmail: new FormControl(),
    taxRepresentativePhoneNumber: new FormControl(),
    taxRepresentativeStreetAddress: new FormControl(),
    taxRepresentativeAptUnitSuite: new FormControl(),
    taxRepresentativeCity: new FormControl(),
    taxRepresentativeState: new FormControl(),
    taxRepresentativeZipCode: new FormControl(),
  });

  agreements = new FormGroup({
    confirmRead: new FormControl(null, [Validators.required]),
    confirmAllInformation: new FormControl(null, [Validators.required]),
    printedName: new FormControl("", [Validators.required]),
  });

  radioChangeAddress(event: any) {
    this.addressType = event.value;
    if (event.value == "1") {
      this.ownerForm.get("addressName")?.clearValidators();
      this.ownerForm.get("addressName")?.updateValueAndValidity();

      this.ownerForm.get("addressOrPoBox")?.clearValidators();
      this.ownerForm.get("addressOrPoBox")?.updateValueAndValidity();

      this.ownerForm.get("cityStateProvince")?.clearValidators();
      this.ownerForm.get("cityStateProvince")?.updateValueAndValidity();

      this.ownerForm.get("country")?.clearValidators();
      this.ownerForm.get("country")?.updateValueAndValidity();

      this.ownerForm.get("ownerStreetAddress")?.addValidators(Validators.required);
      this.ownerForm.get("ownerStreetAddress")?.updateValueAndValidity();

      this.ownerForm.get("city")?.addValidators(Validators.required);
      this.ownerForm.get("city")?.updateValueAndValidity();

      this.ownerForm.get("state")?.addValidators(Validators.required);
      this.ownerForm.get("state")?.updateValueAndValidity();

      this.ownerForm.get("zipCode")?.addValidators(Validators.required);
      this.ownerForm.get("zipCode")?.addValidators(Validators.maxLength(12));
      this.ownerForm.get("zipCode")?.addValidators(Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/));
      this.ownerForm.get("zipCode")?.updateValueAndValidity();
    }
    if (event.value == "2") {
      this.ownerForm.get("addressName")?.addValidators(Validators.required);
      this.ownerForm.get("addressName")?.updateValueAndValidity();

      this.ownerForm.get("addressOrPoBox")?.addValidators(Validators.required);
      this.ownerForm.get("addressOrPoBox")?.updateValueAndValidity();

      this.ownerForm.get("cityStateProvince")?.addValidators(Validators.required);
      this.ownerForm.get("cityStateProvince")?.updateValueAndValidity();

      this.ownerForm.get("country")?.addValidators([Validators.required, Validators.min(1)]);
      this.ownerForm.get("country")?.updateValueAndValidity();

      this.ownerForm.get("ownerStreetAddress")?.clearValidators();
      this.ownerForm.get("ownerStreetAddress")?.updateValueAndValidity();

      this.ownerForm.get("city")?.clearValidators();
      this.ownerForm.get("city")?.updateValueAndValidity();

      this.ownerForm.get("state")?.clearValidators();
      this.ownerForm.get("state")?.updateValueAndValidity();

      this.ownerForm.get("zipCode")?.clearValidators();
      this.ownerForm.get("zipCode")?.updateValueAndValidity();
    }
  }

  goBack(stepper: MatStepper) {
    stepper.previous();
  }
  goForward(stepper: MatStepper) {
    stepper.next();
  }
  ngOnInit(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      positionStrategy: this.overlay.position().global().top("20px").right("30px"),
    });
    this.showNotification(
      "During the registration process you will be required to add at least one property to complete your account setup. Once your account is verified, you will have the flexibility to add as many properties as you wish within our platform.",
      "Important",
      NotificationType.InfoStepper
    );

    this.commonServices.getAllCountry().subscribe((data) => {
      this.countries = data.sort((a, b) => a.name.localeCompare(b.name));
    });
    this.commonServices.getAllState(233).subscribe((data) => {
      this.states = data.sort((a, b) => a.name.localeCompare(b.name));
    });
  }
  showAndHideTaxForm(show: boolean) {
    this.showTaxRepresentativeForm = show;

    if (this.showTaxRepresentativeForm) {
      this.mgtnCoReporting.get("taxRepresentativeFirstName")?.addValidators(Validators.required);
      this.mgtnCoReporting.get("taxRepresentativeLastName")?.addValidators(Validators.required);
      this.mgtnCoReporting.get("taxRepresentativeEmail")?.addValidators(Validators.required);
      this.mgtnCoReporting.get("taxRepresentativeEmail")?.addValidators(Validators.email);
      this.mgtnCoReporting.get("taxRepresentativePhoneNumber")?.addValidators(Validators.required);
      this.mgtnCoReporting.get("taxRepresentativePhoneNumber")?.addValidators(Validators.maxLength(100));
      this.mgtnCoReporting
        .get("taxRepresentativePhoneNumber")
        ?.addValidators(
          Validators.pattern(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/)
        );
      this.mgtnCoReporting.get("taxRepresentativeStreetAddress")?.addValidators(Validators.required);
      this.mgtnCoReporting.get("taxRepresentativeState")?.addValidators(Validators.required);
      this.mgtnCoReporting.get("taxRepresentativeCity")?.addValidators(Validators.required);
      this.mgtnCoReporting.get("taxRepresentativeZipCode")?.addValidators(Validators.required);
      this.mgtnCoReporting.get("taxRepresentativeZipCode")?.addValidators(Validators.maxLength(12));
      this.mgtnCoReporting
        .get("taxRepresentativeZipCode")
        ?.addValidators(Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/));
    } else {
      this.mgtnCoReporting.get("taxRepresentativeFirstName")?.clearValidators();
      this.mgtnCoReporting.get("taxRepresentativeLastName")?.clearValidators();
      this.mgtnCoReporting.get("taxRepresentativeEmail")?.clearValidators();
      this.mgtnCoReporting.get("taxRepresentativePhoneNumber")?.clearValidators();
      this.mgtnCoReporting.get("taxRepresentativeAptUnitSuite")?.clearValidators();
      this.mgtnCoReporting.get("taxRepresentativeState")?.clearValidators();
      this.mgtnCoReporting.get("taxRepresentativeCity")?.clearValidators();
      this.mgtnCoReporting.get("taxRepresentativeZipCode")?.clearValidators();
    }
  }
  entityRentalPropertyLicense = new FormGroup(
    {
      departmentOfBusiness: new FormControl("", [Validators.pattern(/^[a-zA-Z1-9][a-zA-Z0-9]*$/)]),
      taxCertificateNumber: new FormControl(""),
      stateCertificateHasBeenApplied: new FormControl(null),
      reasonWhy: new FormControl(""),
      certificateOfRegistration: new FormControl(""),
      localBusinessTaxReceipt: new FormControl(""),
      cityOfRegistration: new FormControl(""),
      primaryContactFirstName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
      primaryContactLastName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
      primaryContactEmail: new FormControl("", [Validators.required, Validators.maxLength(320), Validators.email]),
      primaryContactPhoneNumber: new FormControl("", [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/),
      ]),
    },
    { validators: atLeastOneRequiredValidator(["reasonWhy", "stateCertificateHasBeenApplied", "taxCertificateNumber"]) }
  );

  rentalPropertyLicense = new FormGroup(
    {
      departmentOfBusiness: new FormControl("", [Validators.pattern(/^[a-zA-Z1-9][a-zA-Z0-9]*$/)]),
      taxCertificateNumber: new FormControl(""),
      stateCertificateHasBeenApplied: new FormControl(null),
      reasonWhy: new FormControl(""),
      certificateOfRegistration: new FormControl(""),
      localBusinessTaxReceipt: new FormControl(""),
      cityOfRegistration: new FormControl(""),
    },
    { validators: atLeastOneRequiredValidator(["reasonWhy", "stateCertificateHasBeenApplied", "taxCertificateNumber"]) }
  );
  rentalProperty = new FormGroup({
    propertyStreetAddress: new FormControl("", [Validators.required]),
    aptUnitSuite: new FormControl(""),
    city: new FormControl("", [Validators.required]),
    state: new FormControl(1436, [Validators.required]),
    zipCode: new FormControl("", [
      Validators.required,
      Validators.maxLength(400),
      Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/),
    ]),
    startedRenting: new FormControl(null, [Validators.required]),
    filingFrequency: new FormControl("", [Validators.required]),
    businessName: new FormControl(""),
    webUrl: new FormControl(""),
    usePlatformToRent: new FormControl(false),
    platFormLink: new FormControl(""),
    rentalPropertyBusiness: new FormControl(""),
  });
  save() {
    let enrollmentRentalPropertyLicense: EnrollmentRentalPropertyLicense = {
      regulationLicenseNumber: this.rentalPropertyLicense?.get("departmentOfBusiness")?.value ?? "",
      certificateOfRegistration: this.rentalPropertyLicense?.get("cityOfRegistration")?.value ?? "",
      taxCertificateNumber: this.rentalPropertyLicense?.get("taxCertificateNumber")?.value ?? "",
      stateCertificateHasBeenApplied: this.rentalPropertyLicense?.get("stateCertificateHasBeenApplied")?.value ?? false,
      taxReceipt: this.rentalPropertyLicense?.get("localBusinessTaxReceipt")?.value ?? "",
      reasonWhy: this.rentalPropertyLicense?.get("reasonWhy")?.value ?? "",
    };

    let enrollmentEntityRentalPropertyLicense: EnrollmentEntityRentalPropertyLicense = {
      regulationLicenseNumber: this.entityRentalPropertyLicense?.get("departmentOfBusiness")?.value ?? "",
      certicateOfRegistration: this.entityRentalPropertyLicense?.get("cityOfRegistration")?.value ?? "",
      taxCertificateNumber: this.entityRentalPropertyLicense?.get("taxCertificateNumber")?.value ?? "",
      stateCertificateHasBeenApplied:
        this.entityRentalPropertyLicense?.get("stateCertificateHasBeenApplied")?.value ?? false,
      taxReceipt: this.entityRentalPropertyLicense?.get("localBusinessTaxReceipt")?.value ?? "",
      reasonWhy: this.entityRentalPropertyLicense?.get("reasonWhy")?.value ?? "",
      primaryContactFirstName: this.entityRentalPropertyLicense?.get("primaryContactFirstName")?.value ?? "",
      primaryContactLastName: this.entityRentalPropertyLicense?.get("primaryContactLastName")?.value ?? "",
      primaryContactEmail: this.entityRentalPropertyLicense?.get("primaryContactEmail")?.value ?? "",
      primaryContactPhoneNumber: this.entityRentalPropertyLicense?.get("primaryContactPhoneNumber")?.value ?? "",
    };

    let owner: any = {
      firstName: this.ownerForm.get("firstName")?.value ?? "",
      lastName: this.ownerForm.get("lastName")?.value ?? "",
      email: this.ownerForm.get("email")?.value ?? "",
      phoneNumber: this.ownerForm.get("phoneNumber")?.value ?? "",
      internalId: "",
      streetAddress: this.ownerForm.get("ownerStreetAddress")?.value ?? "",
      aptUnitSuite: this.ownerForm.get("aptUnitSuite")?.value ?? "",
      city: this.ownerForm.get("city")?.value ?? "",
      zipCode: this.ownerForm.get("zipCode")?.value ?? "",
      internationalAddressName: this.ownerForm.get("addressName")?.value ?? "",
      internationalPoboxNumber: this.ownerForm.get("addressOrPoBox")?.value ?? "",
      internationalCityStateProvince: this.ownerForm.get("cityStateProvince")?.value ?? "",
      stateId: this.ownerForm.get("state")?.value ?? 0,
      countryId: this.ownerForm.get("country")?.value ?? 0,
      addressType: Number.parseInt(this.addressType) ,
      id: 0,
    };

    let enrollmentRentalProperty: EnrollmentRentalProperty = {
      rentalPropertyStreetAddress: this.rentalProperty?.get("propertyStreetAddress")?.value ?? "",
      aptUnitSute: this.rentalProperty?.get("aptUnitSuite")?.value ?? "",
      city: this.rentalProperty?.get("city")?.value ?? "",
      state: this.rentalProperty.get("state")?.value ?? 0,
      zipCode: this.rentalProperty?.get("zipCode")?.value ?? "",
      startedRenting: this.rentalProperty?.get("startedRenting")?.value ?? "",
      filingFrecuency: this.rentalProperty?.get("filingFrequency")?.value ?? "",
      businessName: undefined,
      webUrl: undefined,
      useDigitalPlatform: false,
      digitalPlatform: "",
      frequency: this.rentalProperty?.get("filingFrequency")?.value ?? "",
      isOwner: false,
      isPropertyManagementCompany: false,
      // Without a tax representative the checkboxes are hidden: correspondence goes to the owner (QA 2026-10-05).
      correspondenceToOwner: (this.mgtnCoReporting.get("isUsingTaxRepresentative")?.value as boolean | null) !== true
        || (this.mgtnCoReporting.get("sendToOwner")?.value ?? false),
      correspondenceToReportingRepresentative: this.mgtnCoReporting.get("sendToReportingRep")?.value ?? false,
    };

    let enrollmentTaxRepresentative: EnrollmentTaxRepresentative = {
      firstName: this.mgtnCoReporting.get("taxRepresentativeFirstName")?.value ?? "",
      lastName: this.mgtnCoReporting.get("taxRepresentativeLastName")?.value ?? "",
      email: this.mgtnCoReporting.get("taxRepresentativeEmail")?.value ?? "",
      phoneNumber: this.mgtnCoReporting.get("taxRepresentativePhoneNumber")?.value ?? "",
      streetAddress: this.mgtnCoReporting.get("taxRepresentativeStreetAddress")?.value ?? "",
      aptUnitSuite: this.mgtnCoReporting.get("taxRepresentativeAptUnitSuite")?.value ?? "",
      city: this.mgtnCoReporting.get("taxRepresentativeCity")?.value ?? "",
      state: this.mgtnCoReporting.get("taxRepresentativeState")?.value
        ? Number.parseInt(this.mgtnCoReporting.get("taxRepresentativeState")?.value ?? "0") ?? 0
        : 0,
      zipCode: this.mgtnCoReporting.get("taxRepresentativeZipCode")?.value ?? "",
    };
    let modelProperty: PropertyEnrollment = {
      id: 0,
      printedName: this.agreements.get("printedName")?.value ?? "",
      businessLegalName: this.mgtmCoAddress.get("businessLegalName")?.value ?? "",
      businessDbaName: this.mgtmCoAddress.get("businessDbaName")?.value ?? "",
      streetAddress: this.mgtmCoAddress.get("businnesStreetAddress")?.value ?? "",
      aptUnitSuite: this.mgtmCoAddress.get("aptUnitSuite")?.value ?? "",
      city: this.mgtmCoAddress.get("city")?.value ?? "",
      stateId: this.mgtmCoAddress.get("state")?.value ?? 0,
      zipCode: this.mgtmCoAddress.get("zipCode")?.value ?? "",
      webSiteUrl: this.mgtmCoAddress.get("websiteUrl")?.value ?? "",
      note: this.mgtmCoAddress.get("note")?.value ?? "",
      enrollmentRentalPropertyLicense: enrollmentRentalPropertyLicense,
      enrollmentEntityRentalPropertyLicenseDto: enrollmentEntityRentalPropertyLicense,
      ownerDto: owner,
      enrollmentRentalProperty: enrollmentRentalProperty,
      sendToOwner: this.mgtnCoReporting.get("sendToOwner")?.value ?? false,
      sendToTaxReportingRepresentative: this.mgtnCoReporting.get("sendToReportingRep")?.value ?? false,
      isUsingTaxRepresentative: this.mgtnCoReporting?.get("isUsingTaxRepresentative")?.value ?? false,
      enrollmentTaxRepresentative: enrollmentTaxRepresentative,
    }; 
    this.enrollment.registerProperty(modelProperty).subscribe({
      next: (response) => {},
      error: (error) => {
        this.showNotification(
          "An error occurred while saving the information. Please try again later.",
          "Property management company",
          NotificationType.Error
        );
      },
      complete: () => {
        this.stepCompleted = true;
      },
    });
  }
  showNotification(message: string, title: string, notificationType: NotificationType): void {
    // Create a Portal instance of our `NotificationComponent`
    const notificationPortal = new ComponentPortal(NotificationComponent);
    // Attach our `NotificationComponent` to the Overlay Portal
    const notificationRef: ComponentRef<NotificationComponent> = this.overlayRef.attach(notificationPortal);
    // Now we can access the component instance
    // and we can pass input for the `message` prop we created.
    notificationRef.instance.message = message;
    notificationRef.instance.title = title;
    notificationRef.instance.type = notificationType;
    notificationRef.instance.overlayRef = this.overlayRef;
    notificationRef.instance.getColor();

    // Set a timeout to detach our component from the overlay
    // after 3000 milliseconds
    setTimeout(() => {
      this.overlayRef.detach();
    }, 10000);
  }
  goToCreateAccuntPage() {
    this.router.navigate(["register/createAccount"]);
  }

  hasUnsavedChanges(): boolean {
    if (this.stepCompleted) {
      return false;
    }
    return [
      this.mgtmCoAddress,
      this.mgtnCoReporting,
      this.entityRentalPropertyLicense,
      this.ownerForm,
      this.rentalProperty,
      this.rentalPropertyLicense,
      this.agreements,
    ].some((form) => form?.dirty);
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasUnsavedChanges()) {
      return true;
    }
    const dialogRef = this.dialogRef.open(DialogConfirmComponent, {
      width: "479px",
      data: {
        title: "Leave registration?",
        message:
          "If you leave now you will start over and lose all the information you have entered.",
        confirmButtonText: "LEAVE & LOSE DATA",
        cancelButtonText: "STAY ON PAGE",
      },
    });
    return dialogRef.afterClosed().pipe(map((result) => result === true));
  }

  @HostListener("window:beforeunload", ["$event"])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = "You have unsaved registration data.";
    }
  }
  // Cancel leaves the flow; canDeactivate asks "Leave registration?" when data was entered
  // (the old dialog opened without data and crashed, leaving a stuck overlay — QA 2026-10-05).
  openDialog(): void {
    this.router.navigate(["register/createAccount"]);
  }
  async selectPlace(place: any) {
    const details = await this.googlePlacesService.getPlaceDetails(place.place_id);
    let selectedZipCode = this.googlePlacesService.extractLocationDetails(details);

    if (selectedZipCode.zip) {
      this.mgtmCoAddress.get("zipCode")?.setValue(selectedZipCode.zip, { emitEvent: false });
    }

    if (selectedZipCode.city) {
      this.mgtmCoAddress.get("city")?.setValue(selectedZipCode.city);
    }

    if (selectedZipCode.stateCode) {
      let stateId = this.states.find((e) => e.iso2 == selectedZipCode.stateCode)?.id ?? 0;
      this.mgtmCoAddress.get("state")?.setValue(stateId);
    }

    if (selectedZipCode.address) {
      this.mgtmCoAddress.get("businnesStreetAddress")?.setValue(selectedZipCode.address);
      this.mgtmCoAddress.get("businnesStreetAddress")?.updateValueAndValidity();
      this.mgtmCoAddress.get("businnesStreetAddress")?.markAsUntouched();
    }
    this.suggestions = [];
  }
  async selectPlaceOwner(place: any) {


    this.suggestions = [];
    const details = await this.googlePlacesService.getPlaceDetails(place.place_id);
    let selectedZipCode = this.googlePlacesService.extractLocationDetails(details);

    if (selectedZipCode.zip) {
      this.ownerForm.get("zipCode")?.setValue(selectedZipCode.zip, { emitEvent: false });
    }

    if (selectedZipCode.city) {
      this.ownerForm.get("city")?.setValue(selectedZipCode.city);
    }

    if (selectedZipCode.stateCode) {
      let stateId = this.states.find((e) => e.iso2 == selectedZipCode.stateCode)?.id ?? 0;
      this.ownerForm.get("state")?.setValue(stateId);
    }
    if (selectedZipCode.address) {
      this.ownerForm.get("ownerStreetAddress")?.setValue(selectedZipCode.address);
      this.ownerForm.get("ownerStreetAddress")?.updateValueAndValidity();
      this.ownerForm.get("ownerStreetAddress")?.markAsUntouched();
    }
  }
}
